import { io, Socket } from 'socket.io-client';

export type RealtimeEventName =
  | 'ticket.created'
  | 'ticket.status_changed'
  | 'ticket.approval_pending'
  | 'ticket.approval_decided'
  | 'ticket.cx_informed'
  | 'ticket.updated'
  // V4.9.5 — @-mention server-push for the My Tags badge.
  | 'mention.created'
  | 'shopify.webhook_received';

export interface RealtimeMessage {
  event: RealtimeEventName;
  data: Record<string, unknown>;
}

export interface SocketSubscribeFilters {
  owner_id?: string;
  event_types?: RealtimeEventName[];
}

export interface CreateRealtimeSocketArgs {
  /**
   * A getter for the current access token. Called on every (re)connection
   * attempt so that after the access token has been refreshed via the axios
   * interceptor, the socket also picks up the new token instead of looping
   * forever with the expired one.
   */
  getToken: () => string | null;
  wsUrl: string;
  filters?: SocketSubscribeFilters;
}

/**
 * Create a socket.io client connected to the backend `/ws` namespace.
 * The backend reads the JWT from either `auth.token` or `query.token`.
 *
 * IMPORTANT: `auth` is provided as a callback (not a static object) so
 * Socket.IO re-evaluates it on every reconnect attempt. Without this, an
 * expired token at the time of first connect causes an infinite
 * "Reconnecting..." loop and the user appears auto-logged-out.
 */
export function createRealtimeSocket({
  getToken,
  wsUrl,
  filters
}: CreateRealtimeSocketArgs): Socket {
  const base = (wsUrl || '').replace(/\/$/, '');
  const url = base ? `${base}/ws` : '/ws';
  const socket = io(url, {
    transports: ['websocket', 'polling'],
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
    withCredentials: true,
    // Dynamic auth — re-evaluated on every reconnect.
    auth: (cb) => cb({ token: getToken() ?? '' })
  });

  // Re-arm the URL query param on each reconnect for proxies that don't
  // forward the auth handshake (e.g. some legacy nginx configs).
  socket.io.on('reconnect_attempt', () => {
    const fresh = getToken() ?? '';
    socket.io.opts.query = { token: fresh };
  });

  socket.on('connect', () => {
    if (filters) {
      socket.emit('subscribe', { filters });
    }
  });

  return socket;
}
